/**
 * Lazily-loaded screens.
 *
 * Kept in its own module so the route table stays readable. Code splitting
 * matters here: the sign-in page should not download the purchase-order form.
 */
import { lazy } from 'react';

const page = (loader, name) => lazy(() => loader().then((module) => ({ default: module[name] })));

export const DashboardPage = page(() => import('../modules/dashboard/DashboardPage.jsx'), 'DashboardPage');

export const SupplierListPage = page(() => import('../modules/suppliers/SupplierListPage.jsx'), 'SupplierListPage');
export const SupplierFormPage = page(() => import('../modules/suppliers/SupplierFormPage.jsx'), 'SupplierFormPage');
export const SupplierDetailPage = page(
  () => import('../modules/suppliers/SupplierDetailPage.jsx'),
  'SupplierDetailPage'
);

export const RequestListPage = page(
  () => import('../modules/purchase-requests/RequestListPage.jsx'),
  'RequestListPage'
);
export const RequestFormPage = page(
  () => import('../modules/purchase-requests/RequestFormPage.jsx'),
  'RequestFormPage'
);
export const RequestDetailPage = page(
  () => import('../modules/purchase-requests/RequestDetailPage.jsx'),
  'RequestDetailPage'
);

export const OrderListPage = page(() => import('../modules/purchase-orders/OrderListPage.jsx'), 'OrderListPage');
export const OrderFormPage = page(() => import('../modules/purchase-orders/OrderFormPage.jsx'), 'OrderFormPage');
export const OrderDetailPage = page(
  () => import('../modules/purchase-orders/OrderDetailPage.jsx'),
  'OrderDetailPage'
);

export const RfqListPage = page(() => import('../modules/rfqs/RfqListPage.jsx'), 'RfqListPage');
export const RfqFormPage = page(() => import('../modules/rfqs/RfqFormPage.jsx'), 'RfqFormPage');
export const RfqDetailPage = page(() => import('../modules/rfqs/RfqDetailPage.jsx'), 'RfqDetailPage');

/**
 * The external vendor portal.
 *
 * Lazily loaded like everything else, and worth more here than anywhere: this
 * is the one screen in the application that is opened by people who are not
 * users of it, often on a phone, often on a bad connection. It has no reason
 * to download the ERP shell, and thanks to code splitting it does not.
 */
export const VendorRfqPage = page(() => import('../modules/rfq-portal/VendorRfqPage.jsx'), 'VendorRfqPage');

/**
 * The public RFQ intake form.
 *
 * The other screen opened by people who are not users of this application, and
 * the one where code splitting matters most: it is linked from an
 * organization's website and is frequently the FIRST page somebody ever loads
 * from this domain, often on a phone. It has no reason to download the ERP
 * shell, and thanks to this it does not.
 */
export const QuotePortalPage = page(() => import('../modules/quote-portal/QuotePortalPage.jsx'), 'QuotePortalPage');
export const CustomerInvoicePortalPage = page(
  () => import('../modules/customer-portal/CustomerInvoicePortalPage.jsx'),
  'CustomerInvoicePortalPage'
);

export const PublicRfqFormPage = page(
  () => import('../modules/rfq-intake-public/PublicRfqFormPage.jsx'),
  'PublicRfqFormPage'
);

// --- external RFQ intake, the organization's side ---------------------------

export const IntakeFormListPage = page(
  () => import('../modules/rfq-intake/IntakeFormListPage.jsx'),
  'IntakeFormListPage'
);

/**
 * The form builder. Its own chunk, and worth being: it carries the whole
 * field-and-mapping editor plus five other tabs, and somebody working the
 * enquiries inbox has no reason to download any of it.
 */
export const IntakeFormBuilderPage = page(
  () => import('../modules/rfq-intake/IntakeFormBuilderPage.jsx'),
  'IntakeFormBuilderPage'
);

export const IntakeSubmissionsPage = page(
  () => import('../modules/rfq-intake/IntakeSubmissionsPage.jsx'),
  'IntakeSubmissionsPage'
);

export const IntakeSubmissionDetailPage = page(
  () => import('../modules/rfq-intake/IntakeSubmissionDetailPage.jsx'),
  'IntakeSubmissionDetailPage'
);

export const ReceiptListPage = page(
  () => import('../modules/goods-receipts/ReceiptListPage.jsx'),
  'ReceiptListPage'
);
export const ReceiptDetailPage = page(
  () => import('../modules/goods-receipts/ReceiptDetailPage.jsx'),
  'ReceiptDetailPage'
);

// --- vendor bills (three-way match) and purchasing settings -----------------
export const BillListPage = page(() => import('../modules/purchase-bills/BillListPage.jsx'), 'BillListPage');
export const BillFormPage = page(() => import('../modules/purchase-bills/BillFormPage.jsx'), 'BillFormPage');
export const BillDetailPage = page(() => import('../modules/purchase-bills/BillDetailPage.jsx'), 'BillDetailPage');
export const MatchExceptionsPage = page(
  () => import('../modules/purchase-bills/MatchExceptionsPage.jsx'),
  'MatchExceptionsPage'
);
export const PurchaseSettingsPage = page(
  () => import('../modules/purchase-bills/PurchaseSettingsPage.jsx'),
  'PurchaseSettingsPage'
);

// --- inventory -------------------------------------------------------------
export const InventoryOverviewPage = page(
  () => import('../modules/inventory/InventoryOverviewPage.jsx'),
  'InventoryOverviewPage'
);
export const StockListPage = page(
  () => import('../modules/inventory/StockListPage.jsx'),
  'StockListPage'
);
export const WarehouseScannerPage = page(
  () => import('../modules/inventory/WarehouseScannerPage.jsx'),
  'WarehouseScannerPage'
);
export const ItemStockPage = page(
  () => import('../modules/inventory/ItemStockPage.jsx'),
  'ItemStockPage'
);
export const LedgerListPage = page(
  () => import('../modules/inventory/LedgerListPage.jsx'),
  'LedgerListPage'
);
export const MovementDetailPage = page(
  () => import('../modules/inventory/MovementDetailPage.jsx'),
  'MovementDetailPage'
);
export const ReservationOrderListPage = page(
  () => import('../modules/inventory/ReservationOrderListPage.jsx'),
  'ReservationOrderListPage'
);
export const ReservationOrderDetailPage = page(
  () => import('../modules/inventory/ReservationOrderDetailPage.jsx'),
  'ReservationOrderDetailPage'
);
export const DeliveryListPage = page(
  () => import('../modules/inventory/DeliveryListPage.jsx'),
  'DeliveryListPage'
);
export const DeliveryFormPage = page(
  () => import('../modules/inventory/DeliveryFormPage.jsx'),
  'DeliveryFormPage'
);
export const DeliveryDetailPage = page(
  () => import('../modules/inventory/DeliveryDetailPage.jsx'),
  'DeliveryDetailPage'
);
export const TransferListPage = page(
  () => import('../modules/inventory/TransferListPage.jsx'),
  'TransferListPage'
);
export const TransferFormPage = page(
  () => import('../modules/inventory/TransferFormPage.jsx'),
  'TransferFormPage'
);
export const TransferDetailPage = page(
  () => import('../modules/inventory/TransferDetailPage.jsx'),
  'TransferDetailPage'
);
export const AdjustmentListPage = page(
  () => import('../modules/inventory/AdjustmentListPage.jsx'),
  'AdjustmentListPage'
);
export const AdjustmentFormPage = page(
  () => import('../modules/inventory/AdjustmentFormPage.jsx'),
  'AdjustmentFormPage'
);
export const AdjustmentDetailPage = page(
  () => import('../modules/inventory/AdjustmentDetailPage.jsx'),
  'AdjustmentDetailPage'
);
export const CountListPage = page(
  () => import('../modules/inventory/CountListPage.jsx'),
  'CountListPage'
);
export const CountFormPage = page(
  () => import('../modules/inventory/CountFormPage.jsx'),
  'CountFormPage'
);
export const CountDetailPage = page(
  () => import('../modules/inventory/CountDetailPage.jsx'),
  'CountDetailPage'
);
export const ReturnListPage = page(
  () => import('../modules/inventory/ReturnListPage.jsx'),
  'ReturnListPage'
);
export const ReturnDetailPage = page(
  () => import('../modules/inventory/ReturnDetailPage.jsx'),
  'ReturnDetailPage'
);
export const StockReceiptListPage = page(
  () => import('../modules/inventory/StockReceiptListPage.jsx'),
  'StockReceiptListPage'
);
export const StockReceiptDetailPage = page(
  () => import('../modules/inventory/StockReceiptDetailPage.jsx'),
  'StockReceiptDetailPage'
);
export const BatchListPage = page(
  () => import('../modules/inventory/BatchListPage.jsx'),
  'BatchListPage'
);
export const SerialListPage = page(
  () => import('../modules/inventory/SerialListPage.jsx'),
  'SerialListPage'
);
export const ValuationPage = page(
  () => import('../modules/inventory/ValuationPage.jsx'),
  'ValuationPage'
);
export const ReorderPage = page(
  () => import('../modules/inventory/ReorderPage.jsx'),
  'ReorderPage'
);
export const InventorySettingsPage = page(
  () => import('../modules/inventory/InventorySettingsPage.jsx'),
  'InventorySettingsPage'
);
export const ExceptionListPage = page(
  () => import('../modules/inventory/ExceptionListPage.jsx'),
  'ExceptionListPage'
);
export const LandedCostListPage = page(
  () => import('../modules/inventory/LandedCostListPage.jsx'),
  'LandedCostListPage'
);
export const LandedCostFormPage = page(
  () => import('../modules/inventory/LandedCostFormPage.jsx'),
  'LandedCostFormPage'
);
export const LandedCostDetailPage = page(
  () => import('../modules/inventory/LandedCostDetailPage.jsx'),
  'LandedCostDetailPage'
);
export const StockReconciliationListPage = page(
  () => import('../modules/inventory/StockReconciliationListPage.jsx'),
  'StockReconciliationListPage'
);
export const StockReconciliationDetailPage = page(
  () => import('../modules/inventory/StockReconciliationDetailPage.jsx'),
  'StockReconciliationDetailPage'
);
export const AbcReportPage = page(
  () => import('../modules/inventory/AbcReportPage.jsx'),
  'AbcReportPage'
);
export const SlowMovingReportPage = page(
  () => import('../modules/inventory/SlowMovingReportPage.jsx'),
  'SlowMovingReportPage'
);

// --- crm -------------------------------------------------------------------
export const CrmDashboardPage = page(
  () => import('../modules/crm/dashboard/CrmDashboardPage.jsx'),
  'CrmDashboardPage'
);
export const LeadsPage = page(() => import('../modules/crm/leads/LeadsPage.jsx'), 'LeadsPage');
export const LeadFormPage = page(() => import('../modules/crm/leads/LeadFormPage.jsx'), 'LeadFormPage');
export const OpportunitiesPage = page(
  () => import('../modules/crm/opportunities/OpportunitiesPage.jsx'),
  'OpportunitiesPage'
);
export const AllBoardsPage = page(
  () => import('../modules/crm/opportunities/AllBoardsPage.jsx'),
  'AllBoardsPage'
);
export const NewBoardPage = page(
  () => import('../modules/crm/opportunities/NewBoardPage.jsx'),
  'NewBoardPage'
);
export const ContactsPage = page(() => import('../modules/crm/contacts/ContactsPage.jsx'), 'ContactsPage');
export const ActivitiesPage = page(
  () => import('../modules/crm/activities/ActivitiesPage.jsx'),
  'ActivitiesPage'
);
export const MeetingsPage = page(
  () => import('../modules/crm/meetings/MeetingsPage.jsx'),
  'MeetingsPage'
);
export const VirtualMeetingRoomPage = page(
  () => import('../modules/crm/meetings/VirtualMeetingRoomPage.jsx'),
  'VirtualMeetingRoomPage'
);
export const PublicMeetingPage = page(
  () => import('../modules/crm/meetings/PublicMeetingPage.jsx'),
  'PublicMeetingPage'
);
export const CampaignsPage = page(
  () => import('../modules/crm/campaigns/CampaignsPage.jsx'),
  'CampaignsPage'
);

export const OrganizationPage = page(
  () => import('../modules/organization/OrganizationPage.jsx'),
  'OrganizationPage'
);
export const BranchesPage = page(() => import('../modules/organization/BranchesPage.jsx'), 'BranchesPage');

export const MembersPage = page(() => import('../modules/users/MembersPage.jsx'), 'MembersPage');
export const RolesPage = page(() => import('../modules/users/RolesPage.jsx'), 'RolesPage');

export const SettingsPage = page(() => import('../modules/settings/SettingsPage.jsx'), 'SettingsPage');

/**
 * How this organization's RFQ process works.
 *
 * Its own chunk, and worth being: it is an administrator's screen, opened
 * rarely, and it carries the whole field-and-rule editor. A buyer raising an
 * RFQ has no reason to download it.
 */
export const RfqSettingsPage = page(
  () => import('../modules/rfq-config/RfqSettingsPage.jsx'),
  'RfqSettingsPage'
);

// Master Data Management (MDM)
export const ItemListPage = page(() => import('../modules/master-data/items/ItemListPage.jsx'), 'ItemListPage');
export const ItemFormPage = page(() => import('../modules/master-data/items/ItemFormPage.jsx'), 'ItemFormPage');
export const ItemDetailPage = page(() => import('../modules/master-data/items/ItemDetailPage.jsx'), 'ItemDetailPage');
export const UomListPage = page(() => import('../modules/master-data/uoms/UomListPage.jsx'), 'UomListPage');
export const UomFormPage = page(() => import('../modules/master-data/uoms/UomFormPage.jsx'), 'UomFormPage');
export const TaxCodeListPage = page(() => import('../modules/master-data/tax-codes/TaxCodeListPage.jsx'), 'TaxCodeListPage');
export const TaxCodeFormPage = page(() => import('../modules/master-data/tax-codes/TaxCodeFormPage.jsx'), 'TaxCodeFormPage');
export const PriceListPage = page(() => import('../modules/master-data/price-lists/PriceListListPage.jsx'), 'PriceListListPage');
export const PriceListListPage = page(() => import('../modules/master-data/price-lists/PriceListListPage.jsx'), 'PriceListListPage');
export const PriceListDetailPage = page(() => import('../modules/master-data/price-lists/PriceListDetailPage.jsx'), 'PriceListDetailPage');
export const CurrencyListPage = page(() => import('../modules/master-data/currencies/CurrencyListPage.jsx'), 'CurrencyListPage');
export const WarehouseListPage = page(() => import('../modules/master-data/warehouses/WarehouseListPage.jsx'), 'WarehouseListPage');
export const WarehouseDetailPage = page(() => import('../modules/master-data/warehouses/WarehouseDetailPage.jsx'), 'WarehouseDetailPage');
export const PartnerListPage = page(() => import('../modules/master-data/partners/PartnerListPage.jsx'), 'PartnerListPage');
export const CustomerListPage = PartnerListPage;
export const PartnerFormPage = page(() => import('../modules/master-data/partners/PartnerFormPage.jsx'), 'PartnerFormPage');
export const CustomerFormPage = PartnerFormPage;
export const PartnerDetailPage = page(() => import('../modules/master-data/partners/PartnerDetailPage.jsx'), 'PartnerDetailPage');
export const CustomerDetailPage = PartnerDetailPage;
export const PaymentTermListPage = page(() => import('../modules/master-data/payment-terms/PaymentTermListPage.jsx'), 'PaymentTermListPage');
export const CostCenterListPage = page(() => import('../modules/master-data/cost-centers/CostCenterListPage.jsx'), 'CostCenterListPage');
export const ShippingMethodListPage = page(() => import('../modules/master-data/shipping-methods/ShippingMethodListPage.jsx'), 'ShippingMethodListPage');
export const TermsTemplateListPage = page(() => import('../modules/master-data/terms-templates/TermsTemplateListPage.jsx'), 'TermsTemplateListPage');

// Human Resources (HR)
export const TeamListPage = page(() => import('../modules/hr/teams/TeamListPage.jsx'), 'TeamListPage');
export const TeamDetailPage = page(() => import('../modules/hr/teams/TeamDetailPage.jsx'), 'TeamDetailPage');
export const TeamFormPage = page(() => import('../modules/hr/teams/TeamFormPage.jsx'), 'TeamFormPage');
export const OrgChartPage = page(() => import('../modules/hr/teams/OrgChartPage.jsx'), 'OrgChartPage');

export const DepartmentListPage = page(() => import('../modules/hr/departments/DepartmentListPage.jsx'), 'DepartmentListPage');
export const DepartmentFormPage = page(() => import('../modules/hr/departments/DepartmentFormPage.jsx'), 'DepartmentFormPage');
export const DesignationListPage = page(() => import('../modules/hr/designations/DesignationListPage.jsx'), 'DesignationListPage');
export const DesignationFormPage = page(() => import('../modules/hr/designations/DesignationFormPage.jsx'), 'DesignationFormPage');
export const EmployeeListPage = page(() => import('../modules/hr/employees/EmployeeListPage.jsx'), 'EmployeeListPage');
export const EmployeeFormPage = page(() => import('../modules/hr/employees/EmployeeFormPage.jsx'), 'EmployeeFormPage');
export const EmployeeDetailPage = page(() => import('../modules/hr/employees/EmployeeDetailPage.jsx'), 'EmployeeDetailPage');
export const EmployeeSeparationPage = page(() => import('../modules/hr/employees/EmployeeSeparationPage.jsx'), 'EmployeeSeparationPage');
export const SeparationListPage = page(() => import('../modules/hr/employees/SeparationListPage.jsx'), 'SeparationListPage');
export const LeaveListPage = page(() => import('../modules/hr/leaves/LeaveListPage.jsx'), 'LeaveListPage');
export const LeaveTypeListPage = page(() => import('../modules/hr/leaves/LeaveTypeListPage.jsx'), 'LeaveTypeListPage');
export const AttendanceListPage = page(() => import('../modules/hr/attendance/AttendanceListPage.jsx'), 'AttendanceListPage');
export const TimesheetListPage = page(() => import('../modules/hr/timesheets/TimesheetListPage.jsx'), 'TimesheetListPage');
export const PayrollComponentsPage = page(() => import('../modules/hr/payroll/PayrollComponentsPage.jsx'), 'PayrollComponentsPage');
export const PayRunListPage = page(() => import('../modules/hr/payroll/PayRunListPage.jsx'), 'PayRunListPage');
export const HolidayListPage = page(() => import('../modules/hr/holidays/HolidayListPage.jsx'), 'HolidayListPage');
export const ExpenseListPage = page(() => import('../modules/hr/expenses/ExpenseListPage.jsx'), 'ExpenseListPage');
export const DocumentTypeListPage = page(() => import('../modules/hr/documents/DocumentTypeListPage.jsx'), 'DocumentTypeListPage');
export const ShiftListPage = page(() => import('../modules/hr/shifts/ShiftListPage.jsx'), 'ShiftListPage');

// HR Self-Service & Manager Services
export const MyDashboardPage = page(() => import('../modules/hr/selfservice/MyDashboardPage.jsx'), 'MyDashboardPage');
export const MyAttendancePage = page(() => import('../modules/hr/selfservice/MyAttendancePage.jsx'), 'MyAttendancePage');
export const MyLeavesPage = page(() => import('../modules/hr/selfservice/MyLeavesPage.jsx'), 'MyLeavesPage');
export const MyTimesheetsPage = page(() => import('../modules/hr/selfservice/MyTimesheetsPage.jsx'), 'MyTimesheetsPage');
export const MyPaySlipsPage = page(() => import('../modules/hr/selfservice/MyPaySlipsPage.jsx'), 'MyPaySlipsPage');
export const MyExpensesPage = page(() => import('../modules/hr/selfservice/MyExpensesPage.jsx'), 'MyExpensesPage');
export const MyProfilePage = page(() => import('../modules/hr/selfservice/MyProfilePage.jsx'), 'MyProfilePage');
export const MyTasksPage = page(() => import('../modules/hr/selfservice/MyTasksPage.jsx'), 'MyTasksPage');
export const TeamTasksPage = page(() => import('../modules/hr/selfservice/TeamTasksPage.jsx'), 'TeamTasksPage');
export const TeamDashboardPage = page(() => import('../modules/hr/selfservice/TeamDashboardPage.jsx'), 'TeamDashboardPage');
export const TeamAttendancePage = page(() => import('../modules/hr/selfservice/TeamAttendancePage.jsx'), 'TeamAttendancePage');
export const TeamApprovalsPage = page(() => import('../modules/hr/selfservice/TeamApprovalsPage.jsx'), 'TeamApprovalsPage');
export const ActivityPage = page(() => import('../modules/settings/ActivityPage.jsx'), 'ActivityPage');
export const AccountPage = page(() => import('../modules/settings/AccountPage.jsx'), 'AccountPage');

// --- Accounting Service Pages ---
export const AccountingOverviewPage = page(
  () => import('../modules/accounting/AccountingOverviewPage.jsx'),
  'AccountingOverviewPage'
);
export const AccountListPage = page(
  () => import('../modules/accounting/accounts/AccountListPage.jsx'),
  'AccountListPage'
);
export const AccountLedgerPage = page(
  () => import('../modules/accounting/accounts/AccountLedgerPage.jsx'),
  'AccountLedgerPage'
);
export const FiscalPage = page(
  () => import('../modules/accounting/fiscal/FiscalPage.jsx'),
  'FiscalPage'
);
export const JournalListPage = page(
  () => import('../modules/accounting/journals/JournalListPage.jsx'),
  'JournalListPage'
);
export const JournalFormPage = page(
  () => import('../modules/accounting/journals/JournalFormPage.jsx'),
  'JournalFormPage'
);
export const JournalDetailPage = page(
  () => import('../modules/accounting/journals/JournalDetailPage.jsx'),
  'JournalDetailPage'
);
export const PostingRuleListPage = page(
  () => import('../modules/accounting/posting-rules/PostingRuleListPage.jsx'),
  'PostingRuleListPage'
);
export const PostingExceptionListPage = page(
  () => import('../modules/accounting/posting-exceptions/PostingExceptionListPage.jsx'),
  'PostingExceptionListPage'
);
export const ArPage = page(
  () => import('../modules/accounting/ar/ArPage.jsx'),
  'ArPage'
);
export const ApPage = page(
  () => import('../modules/accounting/ap/ApPage.jsx'),
  'ApPage'
);
export const BankingPage = page(
  () => import('../modules/accounting/banking/BankingPage.jsx'),
  'BankingPage'
);
export const TaxPage = page(
  () => import('../modules/accounting/tax/TaxPage.jsx'),
  'TaxPage'
);
export const FxPage = page(
  () => import('../modules/accounting/fx/FxPage.jsx'),
  'FxPage'
);
export const FinancialReportsPage = page(
  () => import('../modules/accounting/reports/FinancialReportsPage.jsx'),
  'FinancialReportsPage'
);

// --- sales ---
export const QuotationListPage = page(
  () => import('../modules/sales/QuotationListPage.jsx'),
  'QuotationListPage'
);

export const QuotationDetailPage = page(
  () => import('../modules/sales/QuotationDetailPage.jsx'),
  'QuotationDetailPage'
);

export const SalesOrderListPage = page(
  () => import('../modules/sales/SalesOrderListPage.jsx'),
  'SalesOrderListPage'
);
export const SalesOrderDetailPage = page(
  () => import('../modules/sales/SalesOrderDetailPage.jsx'),
  'SalesOrderDetailPage'
);

export const QuotationFormPage = page(
  () => import('../modules/sales/QuotationFormPage.jsx'),
  'QuotationFormPage'
);

export const SalesReturnListPage = page(
  () => import('../modules/sales/SalesReturnListPage.jsx'),
  'SalesReturnListPage'
);
export const SalesReturnDetailPage = page(
  () => import('../modules/sales/SalesReturnDetailPage.jsx'),
  'SalesReturnDetailPage'
);

export const ApprovalInboxPage = page(
  () => import('../modules/sales/ApprovalInboxPage.jsx'),
  'ApprovalInboxPage'
);

export const ApprovalRulesPage = page(
  () => import('../modules/sales/ApprovalRulesPage.jsx'),
  'ApprovalRulesPage'
);
export const SalesDashboardPage = page(
  () => import('../modules/sales/SalesDashboardPage.jsx'),
  'SalesDashboardPage'
);
export const InvoiceListPage = page(() => import('../modules/sales/InvoiceListPage.jsx'), 'InvoiceListPage');
export const InvoiceDetailPage = page(() => import('../modules/sales/InvoiceDetailPage.jsx'), 'InvoiceDetailPage');
export const InvoicePrintPage = page(() => import('../modules/sales/InvoicePrintPage.jsx'), 'InvoicePrintPage');
export const RecurringInvoicePage = page(() => import('../modules/sales/RecurringInvoicePage.jsx'), 'RecurringInvoicePage');
export const CreditNoteListPage = page(() => import('../modules/sales/CreditNoteListPage.jsx'), 'CreditNoteListPage');
export const CreditSettingsPage = page(() => import('../modules/sales/CreditSettingsPage.jsx'), 'CreditSettingsPage');
export const SalesReportsPage = page(() => import('../modules/sales/SalesReportsPage.jsx'), 'SalesReportsPage');

export const NotFoundPage = page(() => import('../modules/NotFoundPage.jsx'), 'NotFoundPage');

// --- app homes: the landing page of each NexERP app that has no dashboard of
// its own, and Procurement's overview.
export const AppHomePage = page(() => import('../modules/workspace/AppHomePage.jsx'), 'AppHomePage');
export const ProcurementOverviewPage = page(
  () => import('../modules/procurement/ProcurementOverviewPage.jsx'),
  'ProcurementOverviewPage'
);



// --- Analytics & Business Intelligence ---
export const ExecutiveDashboardPage = page(
  () => import('../modules/analytics/ExecutiveDashboardPage.jsx'),
  'ExecutiveDashboardPage'
);
export const FinancialIntelligencePage = page(
  () => import('../modules/analytics/FinancialIntelligencePage.jsx'),
  'FinancialIntelligencePage'
);
export const AgingAndSpendPage = page(
  () => import('../modules/analytics/AgingAndSpendPage.jsx'),
  'AgingAndSpendPage'
);
export const OperationsAnalyticsPage = page(
  () => import('../modules/analytics/OperationsAnalyticsPage.jsx'),
  'OperationsAnalyticsPage'
);
